import React from 'react';
import { observer } from 'mobx-react';
import { When } from 'react-if';
import { theme } from 'antd';
import { DiagramEngine } from '@projectstorm/react-diagrams';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/hooks/use-stores';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import SensorWidgetWithIcon from '@/comp/common/icons/SensorWidgetWithIcon';
import { DeviceConnectivity } from '@/constants/enums';
import ComboSensorWidgetWithIcon from '@/comp/common/icons/ComboSensorWidgetWithIcon';
import MissingDataComboSensorIcon from '@/comp/common/icons/MissingDataComboSensorIcon';
import MissingDataSensorIcon from '@/comp/common/icons/MissingDataSensorIcon';
import { CustomThemeToken } from '@/theme';

import { isSelectedNodeOnMap } from '../../utils/utils';
import { SensorNodeWidgetContainer } from './SensorNodeWidget.styles';
import {
  getWidgetBackgroundColor,
  getWidgetBorderColor,
  SensorNodeWidgetProps,
  getWidgetCircleColor,
  getWidgetBorderStyle,
  flowSensorMissingData,
} from './SensorNodeWidget.utils';
import SensorValue from './SensorValue';
import {permissions} from "@/constants";

const { useToken } = theme;

const SensorNodeWidget = observer(({ node, engine, width, height, data }: SensorNodeWidgetProps) => {
  const { t } = useTranslation();
  const { mapBuilderStore, stationStore, permissionStore } = useStores();
  const hasStationScorePermission = permissionStore.isAuth(permissions.VIEW_STATION_SCORE);
  const entity = node?.getEntity();
  const { connectivity, inMaintenance } = entity;
  const { token } = useToken() as unknown as { token: CustomThemeToken };
  const { currentStationStage } = stationStore;
  const renderedOnMap = !!engine;
  const isSelected = isSelectedNodeOnMap(node.device.deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
  const isConnected = inMaintenance || connectivity === DeviceConnectivity.Connected;
  const isCombo = node.options?.type?.startsWith('combo') && data?.length > 1;
  const showMissingData = currentStationStage?.stage?.order < 4
  const hasMissingData = hasStationScorePermission && showMissingData && flowSensorMissingData(entity);
  const borderColor = getWidgetBorderColor(isSelected, inMaintenance, renderedOnMap, hasMissingData, token);
  const backgroundColor = getWidgetBackgroundColor(isSelected, inMaintenance, renderedOnMap, token);
  const circleColor = getWidgetCircleColor(isConnected, token);
  const borderStyle = getWidgetBorderStyle(hasMissingData);

  const dynamicWidth = hasMissingData ? (isCombo ? 160 : 130) : width;

  const Widget = hasMissingData ? (
    isCombo ? (
      <MissingDataComboSensorIcon
        width={dynamicWidth}
        height={height}
        Icon1={data?.[0]?.Icon}
        Icon2={data?.[1]?.Icon}
        borderColor={borderColor}
        circleColor={circleColor}
        backgroundColor={backgroundColor}
        borderStyle={borderStyle}
        text={t('Missing Info')}
      />
    ) : (
      <MissingDataSensorIcon
        width={dynamicWidth}
        height={height}
        Icon={data?.[0]?.Icon}
        borderColor={borderColor}
        circleColor={circleColor}
        backgroundColor={backgroundColor}
        borderStyle={borderStyle}
        text={t('Missing Info')}
      />
    )
  ) : (
    isCombo ? (
      <ComboSensorWidgetWithIcon
        width={dynamicWidth}
        height={height}
        Icon1={data?.[0]?.Icon}
        Icon2={data?.[1]?.Icon}
        borderColor={borderColor}
        circleColor={circleColor}
        backgroundColor={backgroundColor}
        borderStyle={borderStyle}
      />
    ) : (
      <SensorWidgetWithIcon
        width={dynamicWidth}
        height={height}
        Icon={data?.[0]?.Icon}
        borderColor={borderColor}
        circleColor={circleColor}
        backgroundColor={backgroundColor}
        borderStyle={borderStyle}
      />
    )
  );

  return (
    <SensorNodeWidgetContainer width={dynamicWidth} height={height} $isCombo={isCombo}>
      {Widget}
      <When condition={!!engine}>
        <div className="sensor-values-container">
          {data.map(item => (
            <SensorValue
              metric={item.metric}
              unit={item.unit}
              entity={entity}
              connectivity={connectivity}
              key={item.metric}
            />
          ))}
        </div>

        <AlertsWidget alerts={node.alerts} />

        <Ports node={node} engine={engine as DiagramEngine} width={dynamicWidth} height={height} />
      </When>
    </SensorNodeWidgetContainer>
  );
});

export default SensorNodeWidget;
